import { createApiSlice } from '@/shared/features/query/config/query.build';
import type { ReduxQueryClient } from '@/shared/features/query/domain/interfaces/QueryClient';
import type { AppDispatch } from '@/app/store/store';
import { executeQuery } from '@/shared/features/query/utils/queryUtils';
import { getApiBaseUrl } from '@/core/utils/apiUtils';
import type { BannerWidgetResponseDTO } from '../dto/bannerWidget.dto';
import {
  adaptBannerWidgetCacheResult,
  bannerWidgetsPlainToDomain,
  type BannerWidgetCacheResult,
} from '../mappers/bannerWidget.mapper';
import type { BannerWidgetRepository } from '../../domain/interfaces/BannerWidgetRepository';
import type { BannerWidget } from '../../domain/models/BannerWidget';

export type GetBannerWidgetArgs = {
  tags?: string;
};

function getBannerWidgetUrl(): string {
  const base = getApiBaseUrl();
  // Path always includes /api: .../api/sale/browse-page-banner
  return base ? `${base}/api/sale/browse-page-banner` : '/api/sale/browse-page-banner';
}

const bannerWidgetApiSlice = createApiSlice({
  clientId: 'bannerWidgetApi',
  tagTypes: ['BANNER_WIDGET'],
  endpoints: {
    getBannerWidget: {
      url: getBannerWidgetUrl,
      method: 'GET',
      params: (args: unknown) => {
        const { tags = '' } = (args || {}) as GetBannerWidgetArgs;
        return {
          tags,
        };
      },
      /**
       * DTO -> Domain -> plain objects for Redux.
       * Both widget and browse sections are cached in one entry.
       */
      transformResponse: (response: unknown): BannerWidgetCacheResult => {
        return adaptBannerWidgetCacheResult(
          response as BannerWidgetResponseDTO
        );
      },
      keepUnusedDataFor: 300,
      providesTags: ['BANNER_WIDGET'],
    },
  },
});

export const bannerWidgetApi = bannerWidgetApiSlice.queryClient;
export const bannerWidgetApiRedux = bannerWidgetApiSlice.queryClient as ReduxQueryClient;

export class bannerWidgetAdapter implements BannerWidgetRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async fetchWidgetBanners(params: GetBannerWidgetArgs): Promise<BannerWidget[]> {
    try {
      const initiateResult = bannerWidgetApi.endpoints.getBannerWidget.initiate(params);
      const result = await executeQuery<BannerWidgetCacheResult>(
        initiateResult,
        this.dispatch
      );
      return bannerWidgetsPlainToDomain(result.widget);
    } catch (error) {
      console.error('Error fetching Banner widget banners:', error);
      return [];
    }
  }

  async fetchBrowseBanners(params: GetBannerWidgetArgs): Promise<BannerWidget[]> {
    try {
      const initiateResult = bannerWidgetApi.endpoints.getBannerWidget.initiate(params);
      const result = await executeQuery<BannerWidgetCacheResult>(
        initiateResult,
        this.dispatch
      );
      return bannerWidgetsPlainToDomain(result.browse);
    } catch (error) {
      console.error('Error fetching browse banners:', error);
      return [];
    }
  }
}

